import { FileText, SquarePen } from 'lucide-react';
import type { BreakdownTemplate } from '@jobos/shared';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { isTemplateEditable } from '../templateEditability';

interface Props {
  template: BreakdownTemplate | null;
  onEditContents: (id: string) => void;
}

export function TemplatePreview({ template, onEditContents }: Props) {
  if (!template) {
    return (
      <div className="flex-1 bg-white shadow rounded-lg overflow-hidden flex items-center justify-center text-gray-500">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-sm">Select a template to preview</p>
          <p className="text-xs text-gray-400 mt-2">
            Templates are created from real jobs via "Save as template" on the breakdown page.
          </p>
        </div>
      </div>
    );
  }

  const { snapshot } = template;
  const allItems = [
    ...snapshot.lineItems.map((i) => ({ ...i, projectName: null as string | null })),
    ...snapshot.projects.flatMap((p) =>
      p.lineItems.map((i) => ({ ...i, projectName: p.name }))
    ),
  ];

  return (
    <div className="flex-1 bg-white shadow rounded-lg overflow-hidden flex flex-col">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{formatCurrency(template.totalCost)}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEditContents(template.id)}
              disabled={!isTemplateEditable(template.projectType)}
              title={
                isTemplateEditable(template.projectType)
                  ? undefined
                  : 'Editing this template type is coming soon'
              }
            >
              <SquarePen className="h-4 w-4 mr-1.5" />
              Edit contents
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {template.projectType} · {allItems.length} line item{allItems.length !== 1 ? 's' : ''}
          {template.isDefault && ' · default for this project type'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {allItems.map((item, idx) => (
          <div key={idx} className="border border-gray-200 rounded-md p-3">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs text-gray-500">
                  {item.category}
                  {item.projectName && ` · ${item.projectName}`}
                </div>
                <div className="text-sm text-gray-900 mt-0.5">
                  {item.description || <span className="text-gray-400">(no description)</span>}
                </div>
              </div>
              <div className="text-sm tabular-nums">{formatCurrency(item.totalCost)}</div>
            </div>
            {item.subItems.length > 0 && (
              <div className="mt-2 pl-3 border-l-2 border-gray-100 text-xs text-gray-600 space-y-1">
                {item.subItems.map((s, sidx) => (
                  <div key={sidx} className="flex justify-between">
                    <span>
                      {s.type}
                      {s.note && ` — ${s.note}`}
                    </span>
                    <span className="tabular-nums">
                      {s.amount != null
                        ? formatCurrency(Number(s.amount))
                        : `${s.hours ?? 0}h × ${formatCurrency(Number(s.rate ?? 0))}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
